import cn from "classnames";

import React, { useCallback, useMemo } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, Chip, Tooltip, Typography, useMediaQuery } from "@mui/material";

import { airlineBgColor, bankBgColor } from "@/constants/config";
import { BANK_IMG, BANK_NAME_MAP, PROGRAM_IMG } from "@/constants/programs";
import { PROGRAM_NAME_MAP } from "@/constants/programs";
import useThemeData from "@/hooks/useThemeData";
import { FlightItem } from "@/typings";
import { calculateTargetUrl, formatPts } from "@/utils/index";

import style from "./style.module.scss";

export interface SummaryOptionsProps {
  data: FlightItem;
}

const SummaryOptions = ({ data }: SummaryOptionsProps) => {
  const isMediaMax900 = useMediaQuery("(max-width:1000px)");
  const isMediaMin800 = useMediaQuery("(min-width:900px)");

  const theme = useThemeData();

  const options = useMemo(() => {
    return (data.transfer_options || []).map((ele) => ({
      img: BANK_IMG[ele.program],
      title: BANK_NAME_MAP[ele.program],
      bonus: ele.bonus,
    }));
  }, [data]);

  const maxOptions = isMediaMax900 && isMediaMin800 ? 3 : 5;
  const showOptions = options.length > maxOptions ? maxOptions - 1 : maxOptions;

  const formatNumber = useCallback((number: number) => {
    if (Number.isInteger(number)) {
      return number.toString(); // Return as string without decimal point
    } else {
      return number.toFixed(1); // Format with one digit after the decimal point
    }
  }, []);

  const minPts = useMemo(() => {
    return formatNumber(Math.min(...(data.transfer_options?.map((ele) => ele.points) || [0])));
  }, [data, formatNumber]);

  const toolTipText = useMemo(
    () =>
      options
        .slice(showOptions)
        .map((ele) => ele.title)
        .join("\n"),
    [options, showOptions]
  );

  const programTooltipText = PROGRAM_NAME_MAP.get(data.program_code);

  return (
    <Box
      className="inline-flex rounded-[6px] p-[8px] items-center"
      sx={{
        backgroundColor: theme.isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#FAFAFA",
      }}
    >
      {options && options.length > 0 ? (
        <Box
          className={cn("flex flex-wrap rounded-[6px] pl-[6px] pb-[10px] justify-center")}
          sx={{
            writingMode: "vertical-lr",
            maxHeight: "86px",
            backgroundColor: theme.isDarkMode ? bankBgColor : "#EEEEEE",
          }}
        >
          {options.slice(0, showOptions).map((ele, i) => {
            return (
              <Tooltip
                key={i}
                arrow
                placement="top"
                enterDelay={200}
                enterTouchDelay={400}
                title={<div style={{ whiteSpace: "pre-line" }}>{ele.title}</div>}
              >
                <div key={i} className="relative">
                  {ele.bonus > 0 && (
                    <span
                      className="block absolute top-0 left-[50%] translate-x-[-50%] text-[12px] rounded-[4px] px-[6px] leading-[1.4] scale-[0.6]"
                      style={{
                        writingMode: "horizontal-tb",
                        backgroundColor: theme.isDarkMode
                          ? "rgba(255, 255, 255, 0.16)"
                          : theme.palette.primary.main,
                        color: theme.isDarkMode ? "rgba(255, 255, 255)" : "#fff",
                        whiteSpace: "nowrap",
                        fontWeight: "bold",
                      }}
                    >
                      +{ele.bonus}% bonus
                    </span>
                  )}
                  <img src={ele.img} className={style.optionItem} />
                </div>
              </Tooltip>
            );
          })}

          {options.length > maxOptions ? (
            <Tooltip
              arrow
              enterDelay={400}
              enterTouchDelay={400}
              title={<div style={{ whiteSpace: "pre-line" }}>{toolTipText}</div>}
            >
              <Typography
                color="textSecondary"
                variant="caption"
                textAlign="center"
                fontSize={11}
                fontWeight="bold"
                className={style.optionItem}
              >
                +{options.length - showOptions} options
              </Typography>
            </Tooltip>
          ) : null}

          <Typography
            color="textPrimary"
            variant="caption"
            textAlign="center"
            fontWeight="bold"
            fontSize={12}
            className={style.priceItem}
          >
            {formatPts(Number(minPts))}
          </Typography>
        </Box>
      ) : null}

      {options && options.length > 0 ? (
        <ArrowForwardIcon
          className="ml-[4px] mr-[3px] !text-[10px]"
          sx={{ color: (theme) => theme.palette.text.secondary }}
        />
      ) : null}

      <div>
        <Tooltip placement="top" title={programTooltipText}>
          <Box
            className="rounded-[4px] p-[5px] flex items-center"
            sx={{
              backgroundColor: theme.isDarkMode ? airlineBgColor : "transparent",
            }}
            onClick={(e) => {
              e.stopPropagation();

              const products = data?.fare?.products || [];
              const origin_airport = products[0]?.origin;
              const arrival_airport = products[products.length - 1]?.destination;
              const departure_date = data?.date;
              const program_code = data?.program_code;
              const cabin = data?.payment?.cabin || data?.final_cabin_type;
              // Convert time to date format
              const buy_ticket_url = calculateTargetUrl(
                program_code,
                origin_airport,
                arrival_airport,
                departure_date,
                cabin
              );

              window.open(buy_ticket_url, "_blank");
            }}
          >
            <div className="w-[90px] h-[30px] overflow-hidden">
              <img className="object-contain w-full h-full" src={PROGRAM_IMG[data.program_code]} />
            </div>
          </Box>
        </Tooltip>

        <Typography variant="body2" sx={{ fontWeight: "bold", marginLeft: "12px" }} mt={"2px"}>
          {formatPts(data.award_points)}
        </Typography>

        <Typography variant="body2" color="textSecondary" mt={"0px"} ml={"12px"}>
          +${data.surcharge}
        </Typography>
      </div>
    </Box>
  );
};

export default React.memo(SummaryOptions);
